import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { AnalyticsService } from '../../../api/services/analyticsService';
import { AnalyticsRow } from '../../../types/api/response/analyticsResponse';
import { AsOfChip } from '../../../components/Analytics/AsOfChip';
import { AnalyticsTable } from '../../../components/Analytics/AnalyticsTable';
import { describeAnalyticsError } from '../../../components/Analytics/errors';
import { RefreshButton } from '../../../components/Buttons/RefreshButton';

type Indicator = 'REPACK_NEEDED' | 'CRITICAL' | 'LOW' | 'BULK_ONLY' | 'OK';

const INDICATOR_META: Record<Indicator, { label: string; color: string }> = {
  REPACK_NEEDED: { label: 'Repack needed', color: '#d29922' },
  CRITICAL: { label: 'Critical', color: '#d32f2f' },
  LOW: { label: 'Low stock', color: '#ed6c02' },
  BULK_ONLY: { label: 'Bulk only', color: '#1976d2' },
  OK: { label: 'Good', color: '#2e7d32' },
};

const STOCK_LABEL_COLOR: Record<string, 'error' | 'warning' | 'success'> = {
  OUT: 'error',
  LOW: 'warning',
  OK: 'success',
};

/**
 * "A sync is already running" is true but not actionable: one that started two minutes ago is
 * worth waiting for, one that started ninety minutes ago is stuck and worth reporting. Name the
 * age and who triggered it (the nightly Logic App, or another user) so the reader can tell those
 * apart. Falls back to the plain sentence if the run lookup fails — the message is a courtesy,
 * not a reason to fail the action.
 */
const describeRunningSync = async (runId: number): Promise<string> => {
  try {
    const run = await AnalyticsService.getRunStatus(runId);
    const minutes = dayjs().diff(dayjs(run.started_at), 'minute');
    let age: string;
    if (minutes < 1) age = 'moments ago';
    else if (minutes < 60) age = `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    else {
      const hours = Math.floor(minutes / 60);
      age = `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    const who = run.triggered_by ? ` by ${run.triggered_by}` : '';
    return `Sync already in progress — run ${runId} started ${age}${who}. Waiting for it to finish.`;
  } catch {
    return `Sync already in progress (run ${runId}) — waiting for it to finish`;
  }
};

export const FreshProducePage = () => {
  const dispatch = useDispatch();
  const [families, setFamilies] = useState<AnalyticsRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'repack' | 'critical' | 'low' | 'good'>(
    'all',
  );
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AnalyticsService.queryView('fresh-produce', {
        sort: 'sort_order,display_name',
        limit: 500,
      });
      setFamilies(response.rows);
      setAsOf(response.as_of);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: describeAnalyticsError(e, {
            fallback: 'Failed to load the fresh produce board',
          }),
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadBoard();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [loadBoard]);

  /** Refresh = trigger the composite sync, poll until it finishes, then reload. */
  const onRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const trigger = await AnalyticsService.triggerRefresh();
      dispatch(
        showNotification({
          message:
            trigger.status === 'ALREADY_RUNNING'
              ? await describeRunningSync(trigger.runId)
              : 'Sync started — this takes a few minutes',
          severity: 'info',
        }),
      );
      pollTimer.current = setInterval(async () => {
        try {
          const run = await AnalyticsService.getRunStatus(trigger.runId);
          if (run.status === 'RUNNING') return;
          if (pollTimer.current) clearInterval(pollTimer.current);
          setRefreshing(false);
          if (run.status === 'OK') {
            dispatch(showNotification({ message: 'Sync complete', severity: 'success' }));
            loadBoard();
            setReloadSignal((n) => n + 1);
          } else {
            dispatch(
              showNotification({
                message: `Sync ${run.status}: ${run.error ?? ''}`,
                severity: 'error',
              }),
            );
          }
        } catch {
          /* poll again next tick */
        }
      }, 5000);
    } catch (e: any) {
      setRefreshing(false);
      dispatch(
        showNotification({
          message: describeAnalyticsError(e, {
            fallback: 'Could not start the sync',
            forbidden: 'You do not have the Analytics Refresh permission',
          }),
          severity: 'error',
        }),
      );
    }
  };

  const counts = useMemo(() => {
    const byIndicator = (indicator: Indicator) =>
      families.filter((f) => f.indicator === indicator).length;
    return {
      total: families.length,
      repack: byIndicator('REPACK_NEEDED'),
      critical: byIndicator('CRITICAL'),
      low: byIndicator('LOW'),
      good: byIndicator('OK') + byIndicator('BULK_ONLY'),
    };
  }, [families]);

  const FILTER_INDICATORS: Record<string, string[]> = {
    all: [],
    repack: ['REPACK_NEEDED'],
    critical: ['CRITICAL'],
    low: ['LOW'],
    good: ['OK', 'BULK_ONLY'],
  };

  const visibleFamilies = useMemo(() => {
    let list = families;
    const indicators = FILTER_INDICATORS[statusFilter];
    if (indicators.length > 0) {
      list = list.filter((f) => indicators.includes(String(f.indicator)));
    }
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      list = list.filter((f) => String(f.display_name).toLowerCase().includes(term));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [families, statusFilter, search]);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Fresh Produce Monitor
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <AsOfChip asOf={asOf} />
          {refreshing ? <CircularProgress size={22} /> : <RefreshButton onClick={onRefresh} />}
        </Stack>
      </Box>

      {/* KPI bar — each card filters the list below; click the active one again to clear */}
      <Grid container spacing={2} mb={3}>
        {[
          { key: 'all' as const, label: 'Produce families', value: counts.total, color: '#1976d2' },
          {
            key: 'repack' as const,
            label: 'Repack needed',
            value: counts.repack,
            color: INDICATOR_META.REPACK_NEEDED.color,
          },
          {
            key: 'critical' as const,
            label: 'Critical',
            value: counts.critical,
            color: INDICATOR_META.CRITICAL.color,
          },
          {
            key: 'low' as const,
            label: 'Low stock',
            value: counts.low,
            color: INDICATOR_META.LOW.color,
          },
          {
            key: 'good' as const,
            label: 'Well stocked',
            value: counts.good,
            color: INDICATOR_META.OK.color,
          },
        ].map((kpi) => {
          const selected = statusFilter === kpi.key;
          return (
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={kpi.key}>
              <Card
                variant="outlined"
                onClick={() => setStatusFilter(selected ? 'all' : kpi.key)}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all .15s',
                  borderColor: selected ? kpi.color : undefined,
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? `${kpi.color}0d` : undefined,
                  '&:hover': { borderColor: kpi.color },
                }}
              >
                <Typography variant="h4" fontWeight={800} sx={{ color: kpi.color }}>
                  {loading ? '–' : kpi.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  {kpi.label}
                </Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Controls */}
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
        <TextField
          size="small"
          placeholder="Search produce…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      {/* Family cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {visibleFamilies.map((family) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={String(family.family_key)}>
              <FamilyCard family={family} />
            </Grid>
          ))}
          {visibleFamilies.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography color="text.secondary" textAlign="center" p={4}>
                No produce families match
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Discontinued products */}
      <Accordion sx={{ mt: 4 }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Discontinued products</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AnalyticsTable
            view="fresh-produce-writeoff"
            searchKey="name"
            searchbarPlaceholder="Search discontinued…"
            reloadSignal={reloadSignal}
            hideAsOf
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

/** One produce family: indicator-coloured card with lazy per-SKU expansion. */
const FamilyCard = ({ family }: { family: AnalyticsRow }) => {
  const dispatch = useDispatch();
  const indicator = (family.indicator as Indicator) ?? 'OK';
  const meta = INDICATOR_META[indicator];
  const [expanded, setExpanded] = useState(false);
  const [skus, setSkus] = useState<AnalyticsRow[] | null>(null);

  const toggle = async () => {
    const opening = !expanded;
    setExpanded(opening);
    if (opening && skus === null) {
      try {
        const response = await AnalyticsService.queryView('fresh-produce-sku', {
          filters: { family_key: String(family.family_key) },
          sort: 'pack_role,name',
        });
        setSkus(response.rows);
      } catch (e: any) {
        setSkus([]);
        dispatch(
          showNotification({
            message: describeAnalyticsError(e, {
              fallback: `Failed to load the SKUs for ${family.display_name}`,
            }),
            severity: 'error',
          }),
        );
      }
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{ borderTop: `3px solid ${meta.color}`, cursor: 'pointer', height: '100%' }}
      onClick={toggle}
    >
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Typography fontWeight={700}>{String(family.display_name)}</Typography>
          <Chip
            size="small"
            label={meta.label}
            sx={{ bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 700 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {String(family.sub_category ?? 'Fresh Produce')}
        </Typography>

        <Box display="flex" gap={3} mt={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Retail
            </Typography>
            <Typography fontWeight={700}>
              {Number(family.retail_total).toLocaleString()}{' '}
              <Typography component="span" variant="caption">
                ({String(family.retail_sku_count)} SKU)
              </Typography>
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Bulk
            </Typography>
            <Typography fontWeight={700}>
              {Number(family.bulk_total).toLocaleString()}{' '}
              <Typography component="span" variant="caption">
                ({String(family.bulk_sku_count)} SKU)
              </Typography>
            </Typography>
          </Box>
          {Boolean(Number(family.dq_multi_bulk)) && (
            <Tooltip title="More than one bulk SKU — duplicate product in Cin7, needs cleanup">
              <Chip size="small" color="warning" variant="outlined" label="Duplicate bulk" />
            </Tooltip>
          )}
        </Box>

        <Collapse in={expanded} onClick={(e) => e.stopPropagation()}>
          <Box mt={2} pt={1.5} borderTop="1px solid #eee">
            {skus === null ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={18} />
              </Box>
            ) : (
              skus.map((sku) => (
                <Box
                  key={String(sku.sku)}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                  gap={1}
                >
                  <Typography variant="body2" noWrap title={String(sku.name)}>
                    {String(sku.name)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                    {String(sku.pack_role) === 'BULK' && (
                      <Chip size="small" variant="outlined" label="Bulk" />
                    )}
                    <Chip
                      size="small"
                      color={STOCK_LABEL_COLOR[String(sku.stock_label)] ?? 'default'}
                      label={`${Number(sku.available).toLocaleString()}`}
                    />
                  </Stack>
                </Box>
              ))
            )}
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};
